import React, { useEffect, useState, useRef } from "react";
import { ymd, xlsYmd } from "../lib/dates";
import { toast } from "sonner";
import * as XLSX from "xlsx";
import { Plus, PencilSimple, Trash, CalendarBlank, Printer, CaretLeft, CaretRight, FileArrowUp, Download } from "@phosphor-icons/react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import api, { formatApiError, formatDate } from "../lib/api";
import { useAuth } from "../lib/auth";
import { generatePDF } from "../lib/pdf";

const EVENT_TYPES = [
  { code: "academic", label: "Akademik", color: "#0B8D95" },
  { code: "event", label: "Kegiatan", color: "#8B5CF6" },
  { code: "holiday", label: "Libur", color: "#DC2626" },
  { code: "exam", label: "Ujian", color: "#F59E0B" },
  { code: "meeting", label: "Rapat", color: "#3B82F6" },
];

const MONTHS = ["Januari","Februari","Maret","April","Mei","Juni","Juli","Agustus","September","Oktober","November","Desember"];
const DAYS = ["Min","Sen","Sel","Rab","Kam","Jum","Sab"];

export default function Kalender() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [lembagas, setLembagas] = useState([]);
  const [current, setCurrent] = useState(new Date());
  const [filterLembaga, setFilterLembaga] = useState("all");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});
  const fileInput = useRef();
  const canManage = ["superadmin","admin","hr"].includes(user?.role);

  const load = async () => {
    const y = current.getFullYear();
    const m = current.getMonth();
    const start = ymd(new Date(y, m - 1, 1));
    const end = ymd(new Date(y, m + 2, 0));
    const q = `?start_date=${start}&end_date=${end}` + (filterLembaga !== "all" ? `&lembaga_id=${filterLembaga}` : "");
    const [e, l] = await Promise.all([api.get(`/calendar${q}`), api.get("/lembaga")]);
    setItems(e.data); setLembagas(l.data);
  };
  useEffect(() => { load(); }, [current, filterLembaga]);

  const openNew = (date) => {
    setEditing(null);
    setForm({ title: "", event_type: "academic", start_date: date || ymd(), end_date: "", all_day: true, location: "", description: "", lembaga_id: "" });
    setOpen(true);
  };
  const openEdit = (ev) => { setEditing(ev); setForm({ ...ev, lembaga_id: ev.lembaga_id || "" }); setOpen(true); };
  const save = async () => {
    try {
      const payload = { ...form, lembaga_id: form.lembaga_id || null, end_date: form.end_date || null, color: EVENT_TYPES.find((t)=>t.code === form.event_type)?.color };
      if (editing) await api.put(`/calendar/${editing.id}`, payload);
      else await api.post("/calendar", payload);
      toast.success("Agenda tersimpan"); setOpen(false); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const remove = async (id) => {
    if (!confirm("Hapus agenda?")) return;
    try { await api.delete(`/calendar/${id}`); toast.success("Terhapus"); load(); } catch { toast.error("Gagal"); }
  };

  const printPDF = () => {
    const monthEvents = items.filter((e) => {
      const d = new Date(e.start_date);
      return d.getMonth() === current.getMonth() && d.getFullYear() === current.getFullYear();
    });
    const lembagaMap = Object.fromEntries(lembagas.map((l) => [l.id, l.name]));
    generatePDF({
      title: `Kalender Akademik ${MONTHS[current.getMonth()]} ${current.getFullYear()}`,
      subtitle: filterLembaga === "all" ? "Semua Lembaga" : lembagaMap[filterLembaga],
      headers: ["Tanggal", "Lembaga", "Jenis", "Judul", "Lokasi", "Deskripsi"],
      rows: monthEvents.map((e) => [
        formatDate(e.start_date) + (e.end_date && e.end_date !== e.start_date ? " - " + formatDate(e.end_date) : ""),
        e.lembaga_id ? (lembagaMap[e.lembaga_id] || "-") : "Yayasan",
        EVENT_TYPES.find((t)=>t.code===e.event_type)?.label || e.event_type,
        e.title, e.location || "-", e.description || "-"
      ]),
      meta: { "Total Agenda": monthEvents.length },
      filename: `kalender-${current.getFullYear()}-${current.getMonth()+1}.pdf`,
    });
  };

  const printRekapAll = async () => {
    // Fetch all events for the current year across all lembaga
    const y = current.getFullYear();
    const { data } = await api.get(`/calendar?start_date=${y}-01-01&end_date=${y}-12-31`);
    const lembagaMap = Object.fromEntries(lembagas.map((l) => [l.id, l.name]));
    generatePDF({
      title: `Rekap Kalender Akademik ${y}`,
      subtitle: `Seluruh Lembaga Yayasan`,
      headers: ["Tanggal", "Lembaga", "Jenis", "Judul", "Lokasi"],
      rows: data.sort((a,b)=>a.start_date.localeCompare(b.start_date)).map((e) => [
        formatDate(e.start_date) + (e.end_date && e.end_date !== e.start_date ? " - " + formatDate(e.end_date) : ""),
        e.lembaga_id ? (lembagaMap[e.lembaga_id] || "-") : "Yayasan",
        EVENT_TYPES.find((t)=>t.code===e.event_type)?.label || e.event_type,
        e.title, e.location || "-"
      ]),
      meta: { "Total Agenda Tahun": data.length, "Jumlah Lembaga": lembagas.length },
      filename: `rekap-kalender-${y}.pdf`,
    });
  };

  const downloadTemplate = () => {
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.aoa_to_sheet([
      ["title", "event_type", "lembaga_id", "start_date", "end_date", "location", "description"],
      ["MOS Siswa Baru", "event", lembagas[0]?.id || "", "2025-07-14", "2025-07-16", "Aula", "Masa Orientasi Siswa"],
      ["Libur Nasional Kemerdekaan", "holiday", "", "2025-08-17", "2025-08-17", "-", "17 Agustus"],
    ]);
    XLSX.utils.book_append_sheet(wb, ws, "Kalender");
    XLSX.writeFile(wb, "template-kalender.xlsx");
  };

  const importFile = async (file) => {
    try {
      const buf = await file.arrayBuffer();
      const wb = XLSX.read(buf, { cellDates: true });
      const sheet = wb.Sheets[wb.SheetNames[0]];
      const data = XLSX.utils.sheet_to_json(sheet);
      const rows = data.filter((d) => d.title && d.start_date).map((d) => ({
        title: String(d.title),
        event_type: String(d.event_type || "academic"),
        lembaga_id: d.lembaga_id ? String(d.lembaga_id) : null,
        start_date: d.start_date instanceof Date ? xlsYmd(d.start_date) : String(d.start_date),
        end_date: d.end_date ? (d.end_date instanceof Date ? xlsYmd(d.end_date) : String(d.end_date)) : null,
        all_day: true,
        location: d.location ? String(d.location) : null,
        description: d.description ? String(d.description) : null,
        color: EVENT_TYPES.find((t)=>t.code === (d.event_type || "academic"))?.color,
      }));
      const { data: res } = await api.post("/calendar/bulk", rows);
      toast.success(`${res.inserted} agenda diimport`);
      load();
    } catch (e) { toast.error("Gagal: " + (e.message || "")); }
  };

  // Build calendar grid
  const y = current.getFullYear(), m = current.getMonth();
  const firstDay = new Date(y, m, 1).getDay();
  const daysInMonth = new Date(y, m + 1, 0).getDate();
  const cells = [];
  for (let i = 0; i < firstDay; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);

  const eventsByDate = {};
  items.forEach((e) => {
    const dt = e.start_date.slice(0, 10);
    if (!eventsByDate[dt]) eventsByDate[dt] = [];
    eventsByDate[dt].push(e);
  });

  const today = ymd();

  return (
    <div data-testid="kalender-page">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between mb-6 gap-3">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-[#0B8D95] font-semibold mb-1">Agenda</div>
          <h1 className="font-display text-3xl font-black text-gray-900">Kalender Akademik</h1>
          <p className="text-gray-600 text-sm mt-1">Kelola agenda, jadwal ujian, libur, dan kegiatan yayasan.</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button variant="outline" onClick={printPDF} data-testid="kalender-print-button"><Printer size={16} className="mr-1.5" />Cetak Bulan</Button>
          <Button variant="outline" onClick={printRekapAll} data-testid="kalender-rekap-button"><Printer size={16} className="mr-1.5" />Rekap Tahun</Button>
          {canManage && <>
            <Button variant="outline" onClick={downloadTemplate} data-testid="kalender-template-button"><Download size={16} className="mr-1.5" />Template</Button>
            <input ref={fileInput} type="file" accept=".xlsx,.xls" hidden onChange={(e)=>e.target.files?.[0] && importFile(e.target.files[0])} data-testid="kalender-import-file" />
            <Button variant="outline" onClick={()=>fileInput.current?.click()} data-testid="kalender-import-button"><FileArrowUp size={16} className="mr-1.5" />Import Excel</Button>
            <Button onClick={()=>openNew()} className="bg-[#0B8D95] hover:bg-[#48B8BD] text-white" data-testid="kalender-add-button"><Plus size={16} className="mr-1.5" />Tambah Agenda</Button>
          </>}
        </div>
      </div>

      <div className="bg-white border border-gray-200 rounded-md overflow-hidden">
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <div className="flex items-center gap-3">
            <button onClick={()=>setCurrent(new Date(y, m-1, 1))} className="p-2 hover:bg-gray-100 rounded-md" data-testid="kalender-prev"><CaretLeft size={16} /></button>
            <div className="font-display text-xl font-bold text-gray-900 min-w-[220px] text-center">{MONTHS[m]} {y}</div>
            <button onClick={()=>setCurrent(new Date(y, m+1, 1))} className="p-2 hover:bg-gray-100 rounded-md" data-testid="kalender-next"><CaretRight size={16} /></button>
          </div>
          <div className="flex gap-2 items-center">
            <Select value={filterLembaga} onValueChange={setFilterLembaga}>
              <SelectTrigger className="w-48 bg-white" data-testid="kalender-lembaga-filter"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-white">
                <SelectItem value="all">Semua Lembaga</SelectItem>
                {lembagas.map((l)=><SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>)}
              </SelectContent>
            </Select>
            <Button variant="outline" size="sm" onClick={()=>setCurrent(new Date())}>Hari Ini</Button>
          </div>
        </div>

        <div className="grid grid-cols-7 gap-px bg-gray-200 border-b border-gray-200">
          {DAYS.map((d, i) => (
            <div key={d} className={`bg-gray-50 py-2 text-center text-xs font-semibold uppercase tracking-wider ${i === 0 ? "text-red-600" : "text-gray-600"}`}>{d}</div>
          ))}
        </div>

        <div className="grid grid-cols-7 gap-px bg-gray-200">
          {cells.map((d, idx) => {
            const dateStr = d ? ymd(new Date(y, m, d)) : null;
            const events = dateStr ? (eventsByDate[dateStr] || []) : [];
            const isToday = dateStr === today;
            const isSunday = idx % 7 === 0;
            return (
              <div key={idx} className={`bg-white min-h-[100px] p-2 ${d ? "" : "bg-gray-50"} ${canManage && d ? "cursor-pointer hover:bg-cyan-50" : ""}`} onClick={()=>d && canManage && openNew(dateStr)}>
                {d && (
                  <>
                    <div className={`text-xs font-semibold mb-1 inline-flex items-center justify-center ${isToday ? "bg-[#0B8D95] text-white h-6 w-6 rounded-full" : isSunday ? "text-red-600" : "text-gray-700"}`}>{d}</div>
                    <div className="space-y-1">
                      {events.slice(0, 3).map((e) => (
                        <div key={e.id} onClick={(ev)=>{ev.stopPropagation(); openEdit(e);}} className="text-[10px] px-1.5 py-0.5 rounded truncate cursor-pointer hover:opacity-80" style={{ backgroundColor: (e.color || "#0B8D95") + "20", color: e.color || "#0B8D95" }} data-testid={`event-${e.id}`}>
                          {e.title}
                        </div>
                      ))}
                      {events.length > 3 && <div className="text-[10px] text-gray-500">+{events.length - 3} lainnya</div>}
                    </div>
                  </>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Upcoming events list */}
      <div className="bg-white border border-gray-200 rounded-md mt-6 overflow-hidden">
        <div className="px-5 py-4 border-b border-gray-200 font-display text-lg font-bold">Agenda Bulan Ini</div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-left text-xs uppercase tracking-wider text-gray-500">
              <tr><th className="px-4 py-3 font-semibold">Tanggal</th><th className="px-4 py-3 font-semibold">Jenis</th><th className="px-4 py-3 font-semibold">Judul</th><th className="px-4 py-3 font-semibold">Lokasi</th><th className="px-4 py-3 font-semibold text-right">Aksi</th></tr>
            </thead>
            <tbody>
              {items.filter((e)=>{const d=new Date(e.start_date); return d.getMonth()===m && d.getFullYear()===y;}).map((e) => (
                <tr key={e.id} className="border-t border-gray-100 hover:bg-gray-50">
                  <td className="px-4 py-2.5 font-mono text-xs">{formatDate(e.start_date)}</td>
                  <td className="px-4 py-2.5"><span className="px-2 py-0.5 rounded text-xs font-semibold" style={{ backgroundColor: (e.color || "#0B8D95") + "20", color: e.color || "#0B8D95" }}>{EVENT_TYPES.find((t)=>t.code===e.event_type)?.label || e.event_type}</span></td>
                  <td className="px-4 py-2.5 font-semibold">{e.title}</td>
                  <td className="px-4 py-2.5 text-xs text-gray-500">{e.location || "-"}</td>
                  <td className="px-4 py-2.5 text-right">
                    {canManage && <>
                      <button onClick={()=>openEdit(e)} className="p-1.5 hover:bg-gray-100 rounded" data-testid={`event-edit-${e.id}`}><PencilSimple size={14} /></button>
                      <button onClick={()=>remove(e.id)} className="p-1.5 hover:bg-red-50 rounded text-red-600" data-testid={`event-delete-${e.id}`}><Trash size={14} /></button>
                    </>}
                  </td>
                </tr>
              ))}
              {items.filter((e)=>{const d=new Date(e.start_date); return d.getMonth()===m && d.getFullYear()===y;}).length === 0 && (
                <tr><td colSpan={5} className="px-5 py-10 text-center text-gray-500">Belum ada agenda bulan ini</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-white" data-testid="kalender-dialog">
          <DialogHeader><DialogTitle>{editing ? "Edit Agenda" : "Tambah Agenda"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Judul *</Label><Input value={form.title || ""} onChange={(e)=>setForm({...form, title: e.target.value})} data-testid="event-title-input" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Jenis *</Label>
                <Select value={form.event_type} onValueChange={(v)=>setForm({...form, event_type: v})}>
                  <SelectTrigger className="bg-white"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-white">{EVENT_TYPES.map((t)=><SelectItem key={t.code} value={t.code}>{t.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Lembaga</Label>
                <Select value={form.lembaga_id || "none"} onValueChange={(v)=>setForm({...form, lembaga_id: v === "none" ? "" : v})}>
                  <SelectTrigger className="bg-white"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-white"><SelectItem value="none">Semua Lembaga</SelectItem>{lembagas.map((l)=><SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Tanggal Mulai *</Label><Input type="date" value={form.start_date || ""} onChange={(e)=>setForm({...form, start_date: e.target.value})} data-testid="event-start-input" /></div>
              <div><Label>Tanggal Selesai</Label><Input type="date" value={form.end_date || ""} onChange={(e)=>setForm({...form, end_date: e.target.value})} /></div>
            </div>
            <div><Label>Lokasi</Label><Input value={form.location || ""} onChange={(e)=>setForm({...form, location: e.target.value})} /></div>
            <div><Label>Deskripsi</Label><Textarea value={form.description || ""} onChange={(e)=>setForm({...form, description: e.target.value})} /></div>
          </div>
          <DialogFooter>
            {editing && canManage && <Button variant="outline" onClick={()=>{ remove(editing.id); setOpen(false); }} className="text-red-600 mr-auto">Hapus</Button>}
            <Button variant="outline" onClick={()=>setOpen(false)}>Batal</Button>
            <Button onClick={save} className="bg-[#0B8D95] hover:bg-[#48B8BD] text-white" data-testid="event-save-button">Simpan</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
